<script setup lang="ts">
/**
 * Follow-up card (chat is in "ทักตาม"): the prepared message + what the platform allows, and
 * ส่งทักตาม / แก้ก่อนส่ง / ไม่ต้องทัก. Nothing is sent without a click (automatic follow-up stays behind the
 * owner's switches on the server).
 */
import { computed, nextTick, ref, watch } from 'vue'
import { useCollab } from '../../stores/collab'
import { useClock } from '../../stores/clock'
import { useInbox } from '../../stores/inbox'
import { silentText } from '../../lib/collab'
import Icon from '../ui/Icon.vue'
import ToggleSwitch from '../ui/ToggleSwitch.vue'

const props = defineProps<{ convId: string }>()
const collab = useCollab()
const inbox = useInbox()
const clock = useClock()

const info = computed(() => collab.followUps[props.convId] ?? null)
const loading = computed(() => !!collab.followLoading[props.convId])
const editing = ref(false)
const text = ref('')
const box = ref<HTMLTextAreaElement | null>(null)

// what the card depends on in the row: the round (due time), its window, the chat's follow-up switch — realtime
// (conversation.updated from another screen, the side card's switch) reloads the card, no refresh needed
const round = computed(() => {
  const c = inbox.convs[props.convId]
  if (!c) return ''
  return [c.follow_up_due_at ?? '', c.follow_up_until ?? '', c.follow_up?.enabled ?? '', c.follow_up?.after_minutes ?? ''].join('|')
})
watch(
  () => [props.convId, round.value] as const,
  ([id], old) => {
    editing.value = false
    // another chat: its own text (the same template text does not trigger the draft watch below)
    if (old && old[0] !== id) text.value = collab.followUps[id]?.draft?.text ?? ''
    void collab.loadFollowUp(id)
  },
  { immediate: true },
)
watch(
  () => info.value?.draft?.text,
  (t) => {
    if (!editing.value) text.value = t ?? ''
  },
  { immediate: true },
)

const silent = computed(() => silentText(info.value?.silent_since ?? null, clock.now))
const rule = computed(() => info.value?.rule ?? null)
/**
 * The platform's send window (Messenger/IG 24 h …) ran out while the card is on screen: no send button that would
 * only fail — the card is read again and shows the server's reason (or a newer window, e.g. Human Agent's 7 days).
 */
const lapsed = computed(() => {
  const until = rule.value?.ok ? rule.value.send_until : null
  return !!until && Date.parse(until) <= clock.now
})
watch(lapsed, (v) => {
  if (v) void collab.loadFollowUp(props.convId)
})
const sendable = computed(() => !!rule.value?.ok && !lapsed.value)
const canSend = computed(() => sendable.value && !!text.value.trim() && !collab.busy)
const fmtUntil = new Intl.DateTimeFormat('th-TH', {
  weekday: 'short',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
  timeZone: 'Asia/Bangkok',
})
const until = computed(() => (rule.value?.send_until ? fmtUntil.format(new Date(rule.value.send_until)) : null))
const sourceIcon = computed(() => (info.value?.draft?.source === 'ai' ? 'sparkles' : info.value?.draft?.source === 'canned' ? 'zap' : 'note'))

function autosize(): void {
  const el = box.value
  if (!el) return
  el.style.height = 'auto'
  el.style.height = `${Math.min(el.scrollHeight, 160)}px`
}

function edit(): void {
  editing.value = true
  void nextTick(() => {
    autosize()
    const el = box.value
    if (el) {
      el.focus()
      el.setSelectionRange(el.value.length, el.value.length)
    }
  })
}

async function send(): Promise<void> {
  const d = info.value?.draft
  if (!canSend.value) return
  const changed = !d || text.value.trim() !== d.text.trim()
  const ok = await collab.sendFollowUp(props.convId, text.value, changed ? 'manual' : d.source, changed ? null : d.canned_id)
  if (ok) editing.value = false
}

function onKey(e: KeyboardEvent): void {
  if (e.isComposing) return
  if (e.key === 'Enter' && !e.shiftKey) {
    e.preventDefault()
    void send()
  } else if (e.key === 'Escape') {
    editing.value = false
    text.value = info.value?.draft?.text ?? ''
  }
}
</script>

<template>
  <section class="fu" aria-label="ทักตามลูกค้าที่เงียบไป">
    <span v-if="loading" class="fu-bar" role="status" aria-label="กำลังเตรียมข้อความทักตาม"><span></span></span>
    <div class="fu-h">
      <span class="fu-ic"><Icon name="history" /></span>
      <b>ทักตาม</b>
      <span v-if="silent" class="fu-sub">ลูกค้าเงียบไป {{ silent }}</span>
      <span class="grow"></span>
      <span v-if="info?.draft" class="fu-src" :class="info.draft.source"><Icon :name="sourceIcon" />{{ info.draft.label }}</span>
    </div>

    <template v-if="info?.draft">
      <p v-if="!editing" class="fu-txt">{{ text }}</p>
      <textarea
        v-else
        ref="box"
        v-model="text"
        class="fu-edit"
        rows="2"
        aria-label="ข้อความทักตาม แก้ได้ก่อนส่ง"
        @input="autosize"
        @keydown="onKey"
      ></textarea>
    </template>
    <p v-else-if="!loading && info && !info.due" class="fu-txt muted">แชทนี้ไม่อยู่ในคิวทักตามแล้ว</p>
    <p v-else-if="!loading && !info" class="fu-txt muted">โหลดข้อความทักตามไม่สำเร็จ</p>

    <!-- a chat it can be sent to: only the deadline (Messenger/IG 24 h); the "how it is sent" note (LINE push quota …)
         is gone (owner 17 ต.ค. 69 "เอาออก") — why it cannot be sent always shows -->
    <p v-if="rule && (!rule.ok || until)" class="fu-rule" :class="{ bad: !sendable }">
      <Icon :name="sendable ? 'info' : 'alertCircle'" />
      <span v-if="sendable">ส่งได้ถึง <b>{{ until }}</b></span>
      <span v-else-if="rule.ok">เลยเวลาที่ส่งได้แล้ว (ถึง {{ until }})</span>
      <span v-else>{{ rule.reason }}</span>
    </p>
    <!-- automatic follow-up on, but Messenger/IG past 24 h: Human Agent is for a person's reply, so staff answer -->
    <p v-if="info?.auto.enabled && info.auto.reason" class="fu-rule held">
      <Icon name="alertCircle" />
      <span>ทักตามอัตโนมัติไม่ส่งแชทนี้ · {{ info.auto.reason }}</span>
    </p>

    <div class="fu-act">
      <button
        v-if="info?.auto.allowed"
        class="fu-auto"
        role="switch"
        :aria-checked="info.auto.enabled"
        :title="info.auto.sending ? 'ระบบส่งข้อความทักตามให้เองเมื่อถึงเวลา' : 'ยังไม่เปิดส่งจริง (รอเจ้าของเปิดในตั้งค่า)'"
        @click="collab.setAuto(convId, !info.auto.enabled)"
      >
        <ToggleSwitch :on="info.auto.enabled" size="md" /><span>ทักตามอัตโนมัติแชทนี้</span>
      </button>
      <span class="grow"></span>
      <button class="fu-b ghost" :disabled="collab.busy || !info?.due" @click="collab.skipFollowUp(convId)">ไม่ต้องทัก</button>
      <button v-if="!editing" class="fu-b" :disabled="!sendable || !info?.draft || collab.busy" @click="edit">
        <Icon name="pencil" />แก้ก่อนส่ง
      </button>
      <button class="fu-b pri" :disabled="!canSend" @click="send"><span>ส่งทักตาม</span><Icon name="send" /></button>
    </div>
  </section>
</template>

<style scoped>
.fu {
  position: relative;
  overflow: hidden;
  border: 1px solid #cfe3d8;
  border-radius: 12px;
  background: #f7fbf9;
  padding: 10px 12px 10px 14px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.fu-bar {
  position: absolute;
  inset: 0 0 auto;
  height: 3px;
  overflow: hidden;
}
.fu-bar span {
  display: block;
  width: 30%;
  height: 100%;
  background: var(--ac);
  animation: fu-load 1.1s ease-in-out infinite;
}
@keyframes fu-load {
  from {
    transform: translateX(-100%);
  }
  to {
    transform: translateX(340%);
  }
}
.fu-h {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  height: 24px;
  white-space: nowrap;
}
.fu-ic {
  display: inline-flex;
  color: var(--ac2);
}
.fu-ic :deep(.ic) {
  width: 16px;
  height: 16px;
}
.fu-h b {
  font-size: 13.5px;
  font-weight: 600;
  line-height: 21px;
}
.fu-sub {
  font-size: 12.5px;
  line-height: 19px;
  color: var(--mut);
}
.fu-src {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 22px;
  padding: 0 8px 0 6px;
  border-radius: 6px;
  background: #fff;
  border: 1px solid var(--line);
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;
  color: #3e4d46;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}
.fu-src.ai {
  background: var(--acs);
  border-color: #cfe3d8;
  color: var(--ac2);
}
.fu-src :deep(.ic) {
  width: 13px;
  height: 13px;
}
.fu-txt {
  font-size: 14px;
  line-height: 22px;
  color: #2e3b35;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}
.fu-edit {
  display: block;
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #9fcdb8;
  border-radius: 8px;
  background: #fff;
  font-size: 14px;
  line-height: 22px;
  resize: none;
  outline: none;
  max-height: 160px;
}
.fu-rule {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  font-size: 12.5px;
  line-height: 19px;
  color: var(--mut);
}
.fu-rule :deep(.ic) {
  width: 14px;
  height: 14px;
  margin-top: 2px;
}
.fu-rule b {
  font-weight: 600;
  color: var(--ink);
}
.fu-rule.bad {
  color: #8f2418;
}
.fu-rule.held {
  color: #7a4a00;
}
.fu-act {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: 2px;
}
.fu-auto {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 30px;
  padding: 0 6px;
  border-radius: 8px;
  font-size: 12.5px;
  line-height: 19px;
  color: #3e4d46;
  white-space: nowrap;
}
.fu-auto:hover {
  background: #eef5f1;
}
.fu-b {
  height: 32px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid var(--line);
  background: #fff;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 500;
  line-height: 20px;
  white-space: nowrap;
}
.fu-b:hover:not(:disabled) {
  background: #f7f9f8;
}
.fu-b:disabled {
  opacity: 0.55;
}
.fu-b :deep(.ic) {
  width: 15px;
  height: 15px;
}
.fu-b.ghost {
  border-color: transparent;
  background: transparent;
  color: var(--mut);
}
.fu-b.ghost:hover:not(:disabled) {
  background: #eef2f0;
  color: var(--ink);
}
.fu-b.pri {
  border-color: var(--ac);
  background: var(--ac);
  color: #fff;
  font-weight: 600;
}
.fu-b.pri:hover:not(:disabled) {
  background: var(--ac2);
}
.fu-b.pri:disabled {
  background: #a9c9bb;
  border-color: #a9c9bb;
  opacity: 1;
}
</style>
